import React, { useContext, useEffect, useReducer, useState } from 'react';
import api from '../api';
import { Store } from '../Store';
import LoadingLogo from '../components/general/LoadingLogo';
import MessageBox from '../components/general/MessageBox';
import {
  Container,
  Row,
  Col,
  Form,
  OverlayTrigger,
  Popover,
  Button,
} from 'react-bootstrap';
import KeycapBaseLayer from '../components/customizer/shared/customizerScreen/KeycapBaseLayer';
import StaticKeycapShadows from '../components/customizer/shared/customizerScreen/StaticKeycapShadows';
import { getError } from '../utils';
import { nextColorGridIndex, sortColorsByGroups } from '../utils/colorHelpers';
import {
  calculateLayoutWidth,
  calculateLayoutHeight,
} from '../utils/layoutGeometry';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true, error: '' }; // a kept error would outlive the next successful fetch (the render checks it after loading)
    case 'FETCH_COLORS_SUCCESS':
      return {
        ...state,
        colors: sortColorsByGroups(action.payload),
        loading: false,
      };
    case 'FETCH_LAYOUT_SUCCESS':
      return {
        ...state,
        loading: false,
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
};

export default function ColorsScreen() {
  const [{ loading, error, colors }, dispatch] = useReducer(reducer, {
    loading: true,
    error: '',
    colors: [],
  });

  const { state } = useContext(Store);
  const { userInfo } = state;

  const [selectedColor, setSelectedColor] = useState(null);
  const [hexColorCode, setHexColorCode] = useState(null);
  const [formData, setFormData] = useState({
    hexValue: '',
    pantoneColorCode: '',
    pantoneSKU: '',
    colorNames: [],
  });
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [selectedColorSystem, setSelectedColorSystem] = useState('PMS');
  const [savedColors, setSavedColors] = useState([]);

  let totalVerticalSpacing = 0; // initialization of the totalVerticalSpacing variable. This variable is being used to accumulate the vertical spacing that comes with every row of keycaps and is needed to render the rows of keycaps at the correct height
  // A fixed production layout the colour swatches are rendered on. The hard-coded ID stays: this is an admin-only screen, and a missing document is accepted (user decision, Oct 2026).
  const layoutId = '66392d3200c9f13e3bca6ef2';

  const [layout, setLayout] = useState([]);
  const [layoutWidth, setLayoutWidth] = useState(0);
  const [layoutHeight, setLayoutHeight] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch colors
        const colorsResponse = await api.get(`/api/colors`);

        // Dispatch success action for colors
        dispatch({
          type: 'FETCH_COLORS_SUCCESS',
          payload: colorsResponse.data,
        });

        // Fetch layout
        const layoutResponse = await api.get(`/api/layouts/${layoutId}`);

        // Set layout states
        setLayout(layoutResponse.data.layout);
        setLayoutWidth(calculateLayoutWidth(layoutResponse.data.layout));
        setLayoutHeight(calculateLayoutHeight(layoutResponse.data.layout));

        // Dispatch success action for layout
        dispatch({ type: 'FETCH_LAYOUT_SUCCESS' });
      } catch (err) {
        // Dispatch fail action for both fetches
        dispatch({
          type: 'FETCH_FAIL',
          payload: err.message || getError(err),
        });
      }
    };

    fetchData();
  }, [userInfo, layoutId]);

  const colorGroups = Object.keys(colors);

  const handleSelectColor = (color) => {
    setSelectedColor(color);
    setHexColorCode(color.hexValue);
    setFormData({
      hexValue: color.hexValue,
      pantoneColorCode: color.pantoneColorCode,
      pantoneSKU: color.pantoneSKU,
      colorNames: color.colorNames.join(', '),
    });
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!selectedColor) return;

      // Flatten the colors and filter by selectedColorSystem
      const flatColors = Object.values(colors)
        .flat()
        .filter((color) => color.colorSystem === selectedColorSystem);

      // Find the index of the currently selected color
      const currentIndex = flatColors.findIndex(
        (color) => color.hexValue === selectedColor?.hexValue
      );

      if (currentIndex === -1) return;

      // Calculate the number of columns in the grid
      const gridElement = document.querySelector('.color-grid');
      if (!gridElement || !gridElement.children[0]) return; // Defensive check

      const columnCount = Math.floor(
        gridElement.clientWidth / gridElement.children[0].clientWidth
      );

      if (event.key === 'Enter') {
        handleSaveColor(selectedColor); // Save the color on enter key press
        event.preventDefault();
        return;
      }

      const newIndex = nextColorGridIndex(
        currentIndex,
        flatColors.length,
        columnCount,
        event.key
      ); // the tile an arrow key moves to, null for any other key
      if (newIndex === null) return;
      event.preventDefault();

      if (flatColors[newIndex]) {
        handleSelectColor(flatColors[newIndex]);

        document
          .querySelector(
            `.color-tile[data-hex-value="${flatColors[newIndex].hexValue}"]`
          )
          ?.focus(); // keep the keyboard focus on the tile the arrow keys just selected, so the red border and the focus ring stay on the same tile
      }
    };

    window.addEventListener('keydown', handleKeyDown); // on window, so it also takes the arrow keys and Enter from the edit form and the colour-system select (Known bug, KNOWN_BUGS.md, the colour catalogue's keyboard handler)

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedColor, colors, selectedColorSystem]);

  const toggleCollapse = (group) => {
    setCollapsedGroups((prevState) => ({
      ...prevState,
      [group]: !prevState[group],
    }));
  };

  const handleColorSystemChange = (e) => {
    setSelectedColorSystem(e.target.value);
  };

  const handleSaveColor = (color) => {
    setSavedColors((prev) =>
      prev.includes(color.hexValue)
        ? prev.filter((c) => c !== color.hexValue)
        : [...prev, color.hexValue]
    );
  };

  return (
    <Container>
      <div>
        <title>Colors</title>

        <h1 className="screen-heading">Colors</h1>

        {loading ? (
          <div className="overlay">
            <LoadingLogo />
          </div>
        ) : error ? (
          <MessageBox className="danger-alert">{error}</MessageBox>
        ) : (
          <>
            <div className="d-flex align-items-center mb-5">
              <Form.Group
                controlId="colorSystemSelect"
                style={{ width: '100%' }}
              >
                <Form.Control
                  as="select"
                  value={selectedColorSystem}
                  onChange={handleColorSystemChange}
                >
                  <option value="PMS">PMS</option>
                  <option value="TCX">TCX</option>
                </Form.Control>
              </Form.Group>
              <OverlayTrigger
                trigger="click"
                rootClose // close the popover when the user clicks outside of it
                placement="bottom"
                overlay={
                  <Popover>
                    <Popover.Body>
                      <h5>PMS:</h5>
                      <ul>
                        <li>
                          If your keycaps are part of a brand identity or need
                          to match printed materials such as brochures,
                          packaging, or promotional items, PMS colors are
                          relevant. Many brands use PMS colors for their logos
                          and marketing materials, and translating these colors
                          to plastics can ensure brand consistency.
                        </li>
                      </ul>
                      <h5>TCX:</h5>
                      <ul>
                        <li>
                          If your keycap designs are influenced by trends in
                          textiles, fashion, or home interiors, you might want
                          to use TCX colors. This is particularly useful if you
                          are aiming to match keycaps with other products in
                          these categories.
                        </li>
                      </ul>
                    </Popover.Body>
                  </Popover>
                }
              >
                <Button className="info-popover">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="#707070"
                    className="bi bi-info-circle"
                    viewBox="0 0 16 16"
                  >
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                    <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                  </svg>
                </Button>
              </OverlayTrigger>
            </div>
            {selectedColor && (
              <Row className="my-5">
                <Col md={10}>
                  <div
                    className="d-flex justify-content-center"
                    style={{
                      position: 'relative',
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg" // Ensure correct namespace
                      viewBox={`0 0 ${layoutWidth} ${layoutHeight}`}
                      style={{
                        maxWidth: layoutWidth * 3,
                        width: '100%',
                        height: 'auto',
                        overflow: 'visible', // To prevent clipping (is needed to ensure that the svg is not being clipped at the bottom and right side due to rendering nuances like rounding errors and anti-aliasing)
                      }}
                      id="customDesignedKeycapSet"
                    >
                      {
                        <KeycapBaseLayer
                          keycapData={layout}
                          totalVerticalSpacing={totalVerticalSpacing}
                          dataComingFromLayoutEditor={false}
                          dataComingFromColorPicker={true}
                          visibleGroup={null}
                          hexColorCode={hexColorCode}
                          dataComingFromLegendsEditor={false}
                        />
                      }

                      {
                        (totalVerticalSpacing = 0) /* Reset totalVerticalSpacing to 0, becaus it will be reused in the next layout.map function */
                      }

                      {
                        <StaticKeycapShadows
                          keycapData={layout}
                          triggerKeycapShadowRerender={null}
                          handleKeycapClick={null}
                          dataComingFromLayoutEditor={true}
                          dataComingFromLegendsEditor={false}
                        />
                      }
                    </svg>
                  </div>
                </Col>
                <Col
                  className="d-flex align-items-center"
                  style={{ fontSize: '12px' }}
                  md={2}
                >
                  <Form className="selected-color-form">
                    <Form.Group controlId="hexValue">
                      <Form.Label>Hex Value</Form.Label>
                      <Form.Control
                        type="text"
                        name="hexValue"
                        value={formData.hexValue}
                        required
                      />
                    </Form.Group>
                    <Form.Group controlId="pantoneColorCode">
                      <Form.Label>Pantone Color Code</Form.Label>
                      <Form.Control
                        type="text"
                        name="pantoneColorCode"
                        value={formData.pantoneColorCode}
                        required
                      />
                    </Form.Group>
                    <Form.Group controlId="pantoneSKU">
                      <Form.Label>Pantone SKU</Form.Label>
                      <Form.Control
                        type="text"
                        name="pantoneSKU"
                        value={formData.pantoneSKU}
                        required
                      />
                    </Form.Group>
                    <Form.Group controlId="colorNames">
                      <Form.Label>Color Names</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        name="colorNames"
                        value={formData.colorNames}
                        required
                      />
                    </Form.Group>
                  </Form>
                </Col>
              </Row>
            )}
            <div>
              {colorGroups.map((group) => (
                <div key={group}>
                  <h2>
                    {/* the heading stays a heading; the button inside it is what collapses the group: */}
                    <button
                      type="button"
                      className="a11y-unstyled-button"
                      onClick={() => toggleCollapse(group)}
                      aria-expanded={!collapsedGroups[group]}
                    >
                      {group}{' '}
                      {collapsedGroups[group] ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          fill="currentColor"
                          className="bi bi-caret-down-fill"
                          viewBox="0 0 16 16"
                        >
                          <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          fill="currentColor"
                          className="bi bi-caret-up-fill"
                          viewBox="0 0 16 16"
                          style={{ marginBottom: '5px' }}
                        >
                          <path d="m7.247 4.86-4.796 5.481c-.566.647-.106 1.659.753 1.659h9.592a1 1 0 0 0 .753-1.659l-4.796-5.48a1 1 0 0 0-1.506 0z" />
                        </svg>
                      )}
                    </button>
                  </h2>
                  {!collapsedGroups[group] && (
                    <div className="color-grid">
                      {colors[group]
                        .filter(
                          (color) => color.colorSystem === selectedColorSystem
                        )
                        .map((color) => (
                          <button
                            key={color.hexValue}
                            type="button"
                            className={`color-tile ${
                              selectedColor &&
                              selectedColor.hexValue === color.hexValue
                                ? 'selected-color'
                                : ''
                            } ${
                              savedColors.includes(color.hexValue)
                                ? 'saved-color'
                                : ''
                            }`}
                            style={{ backgroundColor: `#${color.hexValue}` }}
                            onClick={() => handleSelectColor(color)}
                            onFocus={() => {
                              if (selectedColor?.hexValue !== color.hexValue) {
                                handleSelectColor(color); // selection follows the keyboard focus, so the red border always marks the focused tile
                              }
                            }}
                            data-hex-value={color.hexValue} // lets the arrow-key handler above move the focus to the newly selected tile
                            aria-pressed={
                              selectedColor?.hexValue === color.hexValue
                            }
                            aria-label={
                              color.colorNames?.length
                                ? color.colorNames.join(', ')
                                : `#${color.hexValue}`
                            } // a colour tile has no text of its own, so its names (or hex code) become its accessible name
                          >
                            {savedColors.includes(color.hexValue) && (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                fill={
                                  selectedColor &&
                                  selectedColor.hexValue === color.hexValue
                                    ? 'red'
                                    : 'green'
                                }
                                className="bi bi-check"
                                viewBox="0 0 16 16"
                              >
                                <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425z" />
                              </svg>
                            )}
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Container>
  );
}
